import { useState, useEffect, useCallback, useMemo } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Edit2,
  Trash2,
  Loader2,
  StickyNote,
  Plus,
  X,
  ChevronDown,
  Link2,
  BellOff,
  Lock,
  Eye,
  ListTodo,
  GripVertical,
  ChevronRight,
  Grid3X3,
  MapPin,
  Pin,
  UserRound,
  AlertTriangle,
  Ban,
  FolderOpen,
  Folder,
  Search,
  ArrowUpDown,
  Filter,
  SlidersHorizontal,
  Check,
  ChevronLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { linkify } from "@/lib/linkify";
import { Label } from "@/components/ui/label";
import { ChecklistDisplay, AddNoteDialog, EditChecklistDialog, ShareChecklistDialog } from "./GameDialogs";
import { MapEditor, MapDisplay } from "./MapNote";
import { CharacterDisplay } from "./CharacterNote";
import ScorecardEditor from "./ScorecardEditor";
import NoteOrganizersDialog from "./NoteOrganizersDialog";

const API = `${getApiUrl()}/api`;

/** Render markdown-style bold/italic text */
function RichText({ text }) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} className="font-bold">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("*") && part.endsWith("*")) {
      return <em key={i} className="italic">{part.slice(1, -1)}</em>;
    }
    return <span key={i}>{linkify(part, `gc-${i}`)}</span>;
  });
}

/**
 * Hybrid collision detection: prefer `pointerWithin` when the cursor is
 * inside a droppable (so small chips catch drops reliably), otherwise
 * fall back to `closestCenter` so sortable reordering keeps working
 * when the pointer is in the gap between two list items.
 */
const noteAreaCollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) return pointerCollisions;
  return closestCenter(args);
};

/**
 * dnd-kit modifier that keeps the drag preview centered on the cursor at
 * all times. Without this the preview stays at whatever offset the initial
 * click had — so if the user grabs a wide row by its small left-edge grip,
 * the preview ghost renders way off to the right of where they're pointing
 * and the hit-target for drop chips lands in the wrong place.
 */
const snapCenterToCursor = ({ activatorEvent, draggingNodeRect, transform }) => {
  if (draggingNodeRect && activatorEvent) {
    const activatorCoords = {
      x: "clientX" in activatorEvent ? activatorEvent.clientX : activatorEvent.touches?.[0]?.clientX ?? 0,
      y: "clientY" in activatorEvent ? activatorEvent.clientY : activatorEvent.touches?.[0]?.clientY ?? 0,
    };
    const offsetX = activatorCoords.x - draggingNodeRect.left;
    const offsetY = activatorCoords.y - draggingNodeRect.top;
    return {
      ...transform,
      x: transform.x + offsetX - draggingNodeRect.width / 2,
      y: transform.y + offsetY - draggingNodeRect.height / 2,
    };
  }
  return transform;
};

/** SortableItem wrapper for drag-and-drop */
function SortableItem({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-1 group/sort">
      <button
        {...attributes}
        {...listeners}
        className="mt-2 p-0.5 text-white/30 hover:text-white/70 cursor-grab active:cursor-grabbing shrink-0 transition-colors"
        data-testid={`drag-handle-${id}`}
        title="Drag to reorder"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

/**
 * Reusable "file into organizer" dropdown menu. Renders a folder icon
 * button that opens a nested menu of every organizer (and its subgroups)
 * plus Uncategorized. Used by every content type in the notes area —
 * notes (all subtypes), links, and grids.
 */
function OrganizerMoveMenu({ item, organizers, onMove, testIdPrefix = "" }) {
  if (!organizers || organizers.length === 0) return null;
  const currentOrg = organizers.find(o => o.id === item.noteOrganizerId);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="text-white/30 hover:text-amber-300 transition-colors shrink-0"
          title={currentOrg ? `Filed under: ${currentOrg.name}` : "File in an organizer"}
          data-testid={`${testIdPrefix}move-${item.id}`}
        >
          {item.noteOrganizerId ? <FolderOpen className="w-3.5 h-3.5 text-amber-400/70" /> : <Folder className="w-3.5 h-3.5" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#0A0A0A] border-white/10 min-w-[180px]">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-white/40">
          File in organizer
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => onMove(item, "", "")}
          className={`text-xs ${!item.noteOrganizerId ? "bg-white/5 text-white" : "text-white/70"}`}
        >
          Uncategorized
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-white/5" />
        {organizers.map(org => (
          (org.subgroups && org.subgroups.length > 0) ? (
            <DropdownMenuSub key={org.id}>
              <DropdownMenuSubTrigger className="text-xs text-white/70 focus:text-white focus:bg-white/5">
                <Folder className="w-3 h-3 mr-1.5 text-amber-400/70" />
                {org.name}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="bg-[#0A0A0A] border-white/10 min-w-[180px]">
                <DropdownMenuItem
                  onClick={() => onMove(item, org.id, "")}
                  className={`text-xs ${item.noteOrganizerId === org.id && !item.noteSubgroupId ? "bg-white/5 text-white" : "text-white/70"}`}
                >
                  <span className="italic text-white/50">(no subgroup)</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/5" />
                {org.subgroups.map(sub => (
                  <DropdownMenuItem
                    key={sub.id}
                    onClick={() => onMove(item, org.id, sub.id)}
                    className={`text-xs ${item.noteOrganizerId === org.id && item.noteSubgroupId === sub.id ? "bg-white/5 text-white" : "text-white/70"}`}
                  >
                    {sub.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ) : (
            <DropdownMenuItem
              key={org.id}
              onClick={() => onMove(item, org.id, "")}
              className={`text-xs ${item.noteOrganizerId === org.id ? "bg-white/5 text-white" : "text-white/70"}`}
            >
              <Folder className="w-3 h-3 mr-1.5 text-amber-400/70" />
              {org.name}
            </DropdownMenuItem>
          )
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Droppable wrapper for the note-organizer filter chips. When a note card
 * is dragged over one of these, the drag-end handler files that note into
 * the target organizer (see `handleCombinedDragEnd`).
 */
function DroppableChip({ id, children, className = "", "data-testid": testId, title }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`${className} ${isOver ? "ring-2 ring-amber-400 scale-105" : ""} transition-transform`}
      data-testid={testId}
      title={title}
    >
      {children}
    </div>
  );
}

/**
 * Full-width User Notes section that lives BELOW the main game header on
 * the game detail page. Unifies notes (text, map, checklist), grids
 * (scorecards), and links into a single drag-and-drop sortable list.
 */
export default function GameContentSection({
  game,
  // Note handlers
  onAddNote,
  onEditNote,
  onDeleteNote,
  onToggleChecklistItem,
  onShareChecklist,
  onEditChecklist,
  onAddChecklist,
  onSaveChecklistPrefs,
  checklistTemplates = [],
  // Link handlers
  onAddLink,
  onEditLink,
  onDeleteLink,
  // Map handlers
  onAddMap,
  onAddCharacter,
  onEditCharacter,
  onEditMap,
  onToggleMapPin,
  onSubmitMapToCommunity,
  onToggleNotePlanner,
  // Achievements — used to render the per-character "unlocked" strip on
  // Character notes.
  achievements = [],
  // Reorder
  onReorderContent,
}) {
  const [notesExpanded, setNotesExpanded] = useState(true);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [editingNote, setEditingNote] = useState(null);
  const [editingChecklist, setEditingChecklist] = useState(null);
  const [shareChecklist, setShareChecklist] = useState(null);
  const [initialNoteType, setInitialNoteType] = useState("note");
  const [expandedNoteIds, setExpandedNoteIds] = useState(new Set());
  // Note-organizer state — fetched once per game, refetched after any
  // organizer change or note move. `notesFilterId` narrows the displayed
  // notes to a single organizer (or "uncategorized" for notes with no
  // organizer). `null` shows everything (default).
  const [noteOrganizers, setNoteOrganizers] = useState([]);
  const [organizersDialogOpen, setOrganizersDialogOpen] = useState(false);
  const [expandedOrganizerIds, setExpandedOrganizerIds] = useState(new Set());
  const [notesFilterId, setNotesFilterId] = useState(null);
  // Optional second-level filter — narrows to a specific subgroup inside
  // the currently-selected organizer. Only meaningful when `notesFilterId`
  // is an organizer id (not null and not "__uncategorized__").
  const [notesFilterSubId, setNotesFilterSubId] = useState(null);

  // Links state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [newLinkTitle, setNewLinkTitle] = useState("");
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [newLinkDescription, setNewLinkDescription] = useState("");
  const [editingLink, setEditingLink] = useState(null);
  const [linkIsPrivate, setLinkIsPrivate] = useState(false);
  const [expandedLinks, setExpandedLinks] = useState({});

  // Map state
  const [mapEditorOpen, setMapEditorOpen] = useState(false);
  const [editingMap, setEditingMap] = useState(null);

  // Scorecard/Grid state
  const [scorecards, setScorecards] = useState([]);
  const [expandedGrids, setExpandedGrids] = useState({});
  const [showNewGridForm, setShowNewGridForm] = useState(false);
  const [newGridName, setNewGridName] = useState("");
  const [gridSaving, setGridSaving] = useState(false);

  // Column count for the content grid — persisted per-game in localStorage so
  // each game can have its own preferred density (e.g. wide maps want 1 col,
  // many short notes want 4 cols). Defaults to 1.
  const gameKey = game?.id;
  const [columns, setColumns] = useState(() => {
    if (!gameKey) return 1;
    try {
      const saved = localStorage.getItem(`gameContentCols:${gameKey}`);
      const n = saved ? parseInt(saved, 10) : 1;
      return [1, 2, 3, 4].includes(n) ? n : 1;
    } catch {
      return 1;
    }
  });
  // Re-read preference when the game changes (navigating between game pages).
  useEffect(() => {
    if (!gameKey) return;
    try {
      const saved = localStorage.getItem(`gameContentCols:${gameKey}`);
      const n = saved ? parseInt(saved, 10) : 1;
      setColumns([1, 2, 3, 4].includes(n) ? n : 1);
    } catch { /* ignore */ }
  }, [gameKey]);
  const changeColumns = (n) => {
    setColumns(n);
    if (gameKey) {
      try { localStorage.setItem(`gameContentCols:${gameKey}`, String(n)); } catch { /* ignore */ }
    }
  };

  // Search / sort / type-filter / pagination for the notes list.
  const [searchQuery, setSearchQuery] = useState("");
  const [sortMode, setSortMode] = useState("manual"); // manual | alpha | newest | oldest | type
  const [typeFilter, setTypeFilter] = useState("all"); // all | note | checklist | map | character | link | grid
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    if (!gameKey) return 10;
    try {
      const saved = localStorage.getItem(`gameContentPageSize:${gameKey}`);
      const n = saved ? parseInt(saved, 10) : 10;
      return [5, 10, 20, 50].includes(n) ? n : 10;
    } catch {
      return 10;
    }
  });
  useEffect(() => {
    if (!gameKey) return;
    try {
      const saved = localStorage.getItem(`gameContentPageSize:${gameKey}`);
      const n = saved ? parseInt(saved, 10) : 10;
      setPageSize([5, 10, 20, 50].includes(n) ? n : 10);
    } catch { /* ignore */ }
  }, [gameKey]);
  const changePageSize = (n) => {
    setPageSize(n);
    if (gameKey) {
      try { localStorage.setItem(`gameContentPageSize:${gameKey}`, String(n)); } catch { /* ignore */ }
    }
  };

  const SORT_LABELS = { manual: "Manual order", alpha: "Alphabetical", newest: "Newest", oldest: "Oldest", type: "Note type" };
  const TYPE_LABELS = { all: "All types", note: "Notes", checklist: "Checklists", map: "Maps", character: "Characters", link: "Links", grid: "Grids" };

  // Classify a unified content item into a filterable type key.
  const itemTypeKey = (it) => {
    if (it.__kind === "link") return "link";
    if (it.__kind === "grid") return "grid";
    const nt = it.noteType || "note";
    if (nt === "map") return "map";
    if (nt === "character") return "character";
    if (nt === "checklist") return "checklist";
    return "note";
  };
  const itemLabel = (it) => {
    if (it.__kind === "link") return it.title || it.url || "";
    if (it.__kind === "grid") return it.name || "";
    return it.title || it.text || "";
  };
  const itemTime = (it) => {
    const t = Date.parse(it.createdAt || it.updatedAt || "");
    return isNaN(t) ? 0 : t;
  };
  const itemSearchText = (it) => {
    const parts = [itemLabel(it), it.text, it.content, it.description, it.url, it.name];
    if (Array.isArray(it.checklistItems)) {
      for (const ci of it.checklistItems) { parts.push(ci.text); parts.push(ci.description); }
    }
    return parts.filter(Boolean).join(" ").toLowerCase();
  };

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const notes = useMemo(() => game?.notes || [], [game?.notes]);

  // Mirror game.links locally so the order updates instantly on drag.
  const [links, setLinks] = useState(game?.links || []);
  useEffect(() => {
    setLinks(game?.links || []);
  }, [game?.links]);

  // Scorecard CRUD
  const gameIdForGrids = game?.gameId || game?.id;
  const fetchScorecards = useCallback(async () => {
    if (!gameIdForGrids) return;
    try {
      const res = await axios.get(`${API}/scorecards/game/${gameIdForGrids}`);
      setScorecards(res.data);
    } catch (err) {
      console.error("Failed to fetch scorecards:", err);
    }
  }, [gameIdForGrids]);
  useEffect(() => { fetchScorecards(); }, [fetchScorecards]);

  // Fetch note organizers — same lifecycle. Refetched after any organizer
  // CRUD via the dialog's `onChanged` callback.
  const fetchNoteOrganizers = useCallback(async () => {
    if (!game?.id) return;
    try {
      const res = await axios.get(`${API}/games/${game.id}/note-organizers`);
      setNoteOrganizers(res.data || []);
    } catch (err) {
      console.error("Failed to fetch note organizers:", err);
    }
  }, [game?.id]);
  useEffect(() => { fetchNoteOrganizers(); }, [fetchNoteOrganizers]);

  // Move a note into (or out of) an organizer/subgroup. Uses the parent's
  // note-edit handler which (as of the organizer-only fix) accepts empty
  // text so character and map notes move correctly. Async so the drag-end
  // handler can toast only on real success.
  const moveNoteToOrganizer = async (note, organizerId, subgroupId) => {
    if (!onEditNote) return false;
    try {
      await onEditNote(note.id, "", undefined, undefined, {
        noteOrganizerId: organizerId || "",
        noteSubgroupId: subgroupId || "",
      });
      return true;
    } catch (e) {
      return false;
    }
  };

  // Link organizer move — link update PUT accepts noteOrganizerId/noteSubgroupId.
  const moveLinkToOrganizer = async (link, organizerId, subgroupId) => {
    if (!game?.id) return false;
    try {
      await axios.put(`${API}/games/${game.id}/links/${link.id}`, {
        title: link.title,
        url: link.url,
        noteOrganizerId: organizerId || "",
        noteSubgroupId: subgroupId || "",
      });
      fetchNoteOrganizers();
      onEditNote && onEditNote();  // triggers parent fetchData for grid+link refresh
      return true;
    } catch (e) {
      return false;
    }
  };

  // Grid (scorecard) organizer move — scorecard PUT accepts the same fields.
  const moveGridToOrganizer = async (grid, organizerId, subgroupId) => {
    try {
      await axios.put(`${API}/scorecards/${grid.id}`, {
        noteOrganizerId: organizerId || "",
        noteSubgroupId: subgroupId || "",
      });
      fetchScorecards();
      return true;
    } catch (e) {
      return false;
    }
  };

  const createGrid = async () => {
    setGridSaving(true);
    try {
      const res = await axios.post(`${API}/scorecards`, {
        name: newGridName || "Grid",
        gameId: gameIdForGrids,
      });
      setScorecards(prev => [...prev, res.data]);
      setExpandedGrids(prev => ({ ...prev, [res.data.id]: true }));
      setShowNewGridForm(false);
      setNewGridName("");
      toast.success("Grid created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create grid");
    } finally {
      setGridSaving(false);
    }
  };

  const saveGrid = async (scorecardId, data) => {
    try {
      const res = await axios.put(`${API}/scorecards/${scorecardId}`, data);
      setScorecards(prev => prev.map(s => s.id === scorecardId ? res.data : s));
      toast.success("Grid saved");
    } catch {
      toast.error("Failed to save grid");
    }
  };

  const deleteGrid = async (scorecardId, name) => {
    if (!window.confirm(`Are you sure you want to delete this grid${name ? ` "${name}"` : ""}?\n\nDeleted grids cannot be recovered.`)) return;
    try {
      await axios.delete(`${API}/scorecards/${scorecardId}`);
      setScorecards(prev => prev.filter(s => s.id !== scorecardId));
      toast.success("Grid deleted");
    } catch {
      toast.error("Failed to delete grid");
    }
  };

  const handleSaveNote = (noteText, noteTitle, isPrivate, extras) => {
    if (editingNote) {
      onEditNote(editingNote.id, noteText || newNoteText, noteTitle, isPrivate, extras);
    } else {
      onAddNote(noteText || newNoteText, noteTitle, isPrivate, extras);
    }
    setNoteDialogOpen(false);
    setNewNoteText("");
    setEditingNote(null);
  };

  const openEditNote = (note) => {
    setEditingNote(note);
    setNewNoteText(note.text);
    setNoteDialogOpen(true);
  };

  const handleSaveLink = () => {
    if (editingLink) {
      onEditLink(editingLink.id, newLinkTitle, newLinkUrl, linkIsPrivate, newLinkDescription);
    } else {
      onAddLink(newLinkTitle, newLinkUrl, linkIsPrivate, newLinkDescription);
    }
    setLinkDialogOpen(false);
    setNewLinkTitle("");
    setNewLinkUrl("");
    setNewLinkDescription("");
    setLinkIsPrivate(false);
    setEditingLink(null);
  };

  const openEditLink = (link) => {
    setEditingLink(link);
    setNewLinkTitle(link.title);
    setNewLinkUrl(link.url);
    setNewLinkDescription(link.description || "");
    setLinkIsPrivate(link.isPrivate || false);
    setLinkDialogOpen(true);
  };

  // Unified item list ordered by game.contentOrder
  const combinedItems = useMemo(() => {
    const taggedNotes = (notes || []).map(n => ({ ...n, __kind: "note", __id: n.id }));
    const taggedGrids = (scorecards || []).map(s => ({ ...s, __kind: "grid", __id: s.id }));
    const taggedLinks = (links || []).map(l => ({ ...l, __kind: "link", __id: l.id }));
    const all = [...taggedNotes, ...taggedGrids, ...taggedLinks];
    const order = Array.isArray(game?.contentOrder) ? game.contentOrder : [];
    if (order.length === 0) return all;
    const byId = new Map(all.map(it => [it.__id, it]));
    const ordered = [];
    const seen = new Set();
    for (const id of order) {
      const item = byId.get(id);
      if (item) {
        ordered.push(item);
        seen.add(id);
      }
    }
    for (const it of all) {
      if (!seen.has(it.__id)) ordered.push(it);
    }
    return ordered;
  }, [notes, scorecards, links, game?.contentOrder]);

  const [optimisticOrder, setOptimisticOrder] = useState(null);

  const displayItems = useMemo(() => {
    const base = !optimisticOrder ? combinedItems : (() => {
      const byId = new Map(combinedItems.map(it => [it.__id, it]));
      const ordered = optimisticOrder.map(id => byId.get(id)).filter(Boolean);
      for (const it of combinedItems) {
        if (!optimisticOrder.includes(it.__id)) ordered.push(it);
      }
      return ordered;
    })();
    // Apply note-organizer filter. Every content type — plain notes,
    // character/map/checklist notes, links, and grids — now respects the
    // active organizer/subgroup filter. Items without a `noteOrganizerId`
    // only appear under "All" or "Uncategorized".
    if (!notesFilterId) return base;
    return base.filter(it => {
      if (notesFilterId === "__uncategorized__") return !it.noteOrganizerId;
      if (it.noteOrganizerId !== notesFilterId) return false;
      if (notesFilterSubId === "__loose__") return !it.noteSubgroupId;
      if (notesFilterSubId) return it.noteSubgroupId === notesFilterSubId;
      return true;
    });
  }, [combinedItems, optimisticOrder, notesFilterId, notesFilterSubId]);

  // Apply search + type filter + sort on top of the organizer-filtered list.
  const filteredSorted = useMemo(() => {
    let arr = displayItems;
    const q = searchQuery.trim().toLowerCase();
    if (q) arr = arr.filter((it) => itemSearchText(it).includes(q));
    if (typeFilter !== "all") arr = arr.filter((it) => itemTypeKey(it) === typeFilter);
    if (sortMode === "alpha") {
      arr = [...arr].sort((a, b) => itemLabel(a).localeCompare(itemLabel(b)));
    } else if (sortMode === "newest") {
      arr = [...arr].sort((a, b) => itemTime(b) - itemTime(a));
    } else if (sortMode === "oldest") {
      arr = [...arr].sort((a, b) => itemTime(a) - itemTime(b));
    } else if (sortMode === "type") {
      arr = [...arr].sort((a, b) => {
        const t = itemTypeKey(a).localeCompare(itemTypeKey(b));
        return t !== 0 ? t : itemLabel(a).localeCompare(itemLabel(b));
      });
    }
    return arr;
  }, [displayItems, searchQuery, typeFilter, sortMode]);

  const pageCount = Math.max(1, Math.ceil(filteredSorted.length / pageSize));
  // Reset to first page whenever the effective filter/search/sort changes.
  useEffect(() => { setPage(1); }, [searchQuery, typeFilter, sortMode, pageSize, notesFilterId, notesFilterSubId]);
  // Clamp the current page if the list shrank.
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);
  const pagedItems = useMemo(
    () => filteredSorted.slice((page - 1) * pageSize, page * pageSize),
    [filteredSorted, page, pageSize]
  );
  const dragEnabled = sortMode === "manual";

  const handleCombinedDragEnd = async (event) => {
    const { active, over } = event;
    if (!over) return;

    // Drop onto a note-organizer filter chip? Droppable ids are prefixed
    // with `note-organizer-drop:`. Two shapes:
    //   note-organizer-drop:<orgId>              — file into organizer (no subgroup)
    //   note-organizer-drop:<orgId>:<subId>      — file into subgroup
    //   note-organizer-drop:__uncategorized__    — remove from any organizer
    //
    // Works for notes, links, and grids — each type has its own move
    // helper that hits the appropriate API endpoint.
    const overIdStr = String(over.id);
    if (overIdStr.startsWith("note-organizer-drop:")) {
      const activeItem = displayItems.find(it => it.__id === active.id);
      if (!activeItem) return;
      const path = overIdStr.slice("note-organizer-drop:".length);
      const [targetOrgId, rawSubId] = path.split(":");
      const targetSubId = rawSubId === "__loose__" ? "" : rawSubId;
      const isUncat = targetOrgId === "__uncategorized__";
      const alreadyThere = isUncat
        ? !activeItem.noteOrganizerId
        : activeItem.noteOrganizerId === targetOrgId
          && (activeItem.noteSubgroupId || "") === (targetSubId || "");
      if (alreadyThere) return;
      const orgIdToSend = isUncat ? "" : targetOrgId;
      const subIdToSend = targetSubId || "";
      let ok = false;
      if (activeItem.__kind === "note") {
        ok = await moveNoteToOrganizer(activeItem, orgIdToSend, subIdToSend);
      } else if (activeItem.__kind === "link") {
        ok = await moveLinkToOrganizer(activeItem, orgIdToSend, subIdToSend);
      } else if (activeItem.__kind === "grid") {
        ok = await moveGridToOrganizer(activeItem, orgIdToSend, subIdToSend);
      }
      if (!ok) {
        toast.error("Failed to move — please try again");
        return;
      }
      const orgName = isUncat
        ? "Uncategorized"
        : (noteOrganizers.find(o => o.id === targetOrgId)?.name || "organizer");
      const subName = targetSubId
        ? (noteOrganizers.find(o => o.id === targetOrgId)?.subgroups?.find(s => s.id === targetSubId)?.name || "subgroup")
        : null;
      toast.success(subName ? `Moved to ${orgName} → ${subName}` : `Moved to ${orgName}`);
      return;
    }

    // Otherwise it's a sortable reorder within the flat list.
    // Manual drag-ordering only applies when the list is in "Manual order".
    if (!dragEnabled) return;
    if (active.id === over.id) return;
    const ids = filteredSorted.map(it => it.__id);
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(ids, oldIndex, newIndex);
    setOptimisticOrder(newOrder);
    onReorderContent?.(newOrder);
  };

  return (
    <div className="mt-6" data-testid="content-section">
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setNotesExpanded(!notesExpanded)}
          className="flex items-center gap-2 text-white/70 hover:text-white text-sm"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${notesExpanded ? "rotate-180" : ""}`} />
          User Notes ({notes.length + links.length + scorecards.length})
        </button>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {notesExpanded && (
            <>
              {/* Search */}
              <div className="relative" data-testid="notes-search-wrap">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-white/30" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search notes..."
                  className="h-7 w-40 sm:w-48 pl-7 pr-6 bg-white/5 border-white/10 text-white text-xs placeholder:text-white/30"
                  data-testid="notes-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                    data-testid="notes-search-clear"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>

              {/* Sort */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 px-2 w-[132px] justify-start text-xs text-white/50 hover:text-white hover:bg-white/5" data-testid="notes-sort-trigger">
                    <ArrowUpDown className="w-3 h-3 mr-1 shrink-0" />
                    <span className="truncate">{SORT_LABELS[sortMode]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#0A0A0A] border-white/10">
                  {Object.entries(SORT_LABELS).map(([mode, label]) => (
                    <DropdownMenuItem
                      key={mode}
                      onClick={() => setSortMode(mode)}
                      className="text-white/70 focus:text-white focus:bg-white/5 text-xs"
                      data-testid={`notes-sort-${mode}`}
                    >
                      {sortMode === mode ? <Check className="w-3.5 h-3.5 mr-2 text-cyan-400" /> : <span className="w-3.5 h-3.5 mr-2" />}
                      {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Filter by note type */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className={`h-7 px-2 w-[116px] justify-start text-xs hover:text-white hover:bg-white/5 ${typeFilter !== "all" ? "text-cyan-300" : "text-white/50"}`} data-testid="notes-filter-trigger">
                    <Filter className="w-3 h-3 mr-1 shrink-0" />
                    <span className="truncate">{TYPE_LABELS[typeFilter]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#0A0A0A] border-white/10">
                  {Object.entries(TYPE_LABELS).map(([key, label]) => (
                    <DropdownMenuItem
                      key={key}
                      onClick={() => setTypeFilter(key)}
                      className="text-white/70 focus:text-white focus:bg-white/5 text-xs"
                      data-testid={`notes-filter-${key}`}
                    >
                      {typeFilter === key ? <Check className="w-3.5 h-3.5 mr-2 text-cyan-400" /> : <span className="w-3.5 h-3.5 mr-2" />}
                      {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* View menu — per-page, per-row, organizers */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-white/50 hover:text-white hover:bg-white/5" data-testid="notes-view-trigger">
                    <SlidersHorizontal className="w-3 h-3 mr-1" />
                    View
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-56 bg-[#0A0A0A] border-white/10 p-3 space-y-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5">Notes per page</p>
                    <div className="flex items-center gap-1" data-testid="notes-perpage-toggle">
                      {[5, 10, 20, 50].map((n) => (
                        <button
                          key={n}
                          onClick={() => changePageSize(n)}
                          className={`flex-1 h-7 text-xs font-medium rounded transition-colors ${pageSize === n ? "bg-cyan-500/20 text-cyan-300" : "text-white/40 hover:text-white/70 bg-white/5 hover:bg-white/10"}`}
                          data-testid={`notes-perpage-${n}`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5">Notes per row</p>
                    <div className="flex items-center gap-1" data-testid="content-columns-toggle">
                      {[1, 2, 3, 4].map((n) => (
                        <button
                          key={n}
                          onClick={() => changeColumns(n)}
                          aria-pressed={columns === n}
                          className={`flex-1 h-7 text-xs font-medium rounded transition-colors ${columns === n ? "bg-cyan-500/20 text-cyan-300" : "text-white/40 hover:text-white/70 bg-white/5 hover:bg-white/10"}`}
                          data-testid={`content-columns-${n}`}
                          title={`Show ${n} column${n > 1 ? "s" : ""} per row`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5">Organizers</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setOrganizersDialogOpen(true)}
                      className="w-full justify-start h-7 px-2 text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10"
                      data-testid="manage-note-organizers-btn"
                    >
                      <FolderOpen className="w-3 h-3 mr-1.5" />
                      {noteOrganizers.length > 0 ? `Manage organizers (${noteOrganizers.length})` : "Manage organizers"}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </>
          )}
          <DropdownMenu>
          {!game?.isAchievementSlop ? (
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-white/50 hover:text-white hover:bg-white/5"
                data-testid="add-content-btn"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add
              </Button>
            </DropdownMenuTrigger>
          ) : (
            <span
              className="inline-flex items-center gap-1 rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-amber-300"
              data-testid="add-content-slop-locked"
              title="This game is flagged as Achievement Slop — notes, checklists, maps, grids, and links cannot be added."
            >
              <Ban className="w-3 h-3" />
              Add disabled
            </span>
          )}
          <DropdownMenuContent align="end" className="bg-[#0A0A0A] border-white/10">
            <DropdownMenuItem
              onClick={() => { setEditingNote(null); setNewNoteText(""); setInitialNoteType("note"); setNoteDialogOpen(true); }}
              className="text-white/70 focus:text-white focus:bg-white/5"
              data-testid="add-note-menu-item"
            >
              <StickyNote className="w-3.5 h-3.5 mr-2 text-amber-400" />
              Note
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => { setEditingNote(null); setNewNoteText(""); setInitialNoteType("checklist"); setNoteDialogOpen(true); }}
              className="text-white/70 focus:text-white focus:bg-white/5"
              data-testid="add-checklist-menu-item"
            >
              <ListTodo className="w-3.5 h-3.5 mr-2 text-violet-400" />
              Checklist
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => { setEditingLink(null); setNewLinkTitle(""); setNewLinkUrl(""); setNewLinkDescription(""); setLinkIsPrivate(false); setLinkDialogOpen(true); }}
              className="text-white/70 focus:text-white focus:bg-white/5"
              data-testid="add-link-menu-item"
            >
              <Link2 className="w-3.5 h-3.5 mr-2 text-blue-400" />
              Link
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => { setEditingMap(null); setMapEditorOpen(true); }}
              className="text-white/70 focus:text-white focus:bg-white/5"
              data-testid="add-map-menu-item"
            >
              <MapPin className="w-3.5 h-3.5 mr-2 text-emerald-400" />
              Map
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onAddCharacter?.()}
              className="text-white/70 focus:text-white focus:bg-white/5"
              data-testid="add-character-menu-item"
            >
              <UserRound className="w-3.5 h-3.5 mr-2" style={{ color: "#e11d74" }} />
              Character
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setShowNewGridForm(true)}
              className="text-white/70 focus:text-white focus:bg-white/5"
              data-testid="add-grid-menu-item"
            >
              <Grid3X3 className="w-3.5 h-3.5 mr-2 text-emerald-400" />
              Grid
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/5" />
            <DropdownMenuItem disabled className="text-white/20 opacity-50">
              <BellOff className="w-3.5 h-3.5 mr-2" />
              Reminder
              <span className="ml-auto text-[9px] uppercase tracking-wider bg-white/5 px-1 py-0.5 rounded">Soon</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </div>

      {game?.isAchievementSlop && (
        <div
          className="mb-2 flex items-start gap-2 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300"
          data-testid="slop-content-locked-banner"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            This game is flagged as <strong>Achievement Slop</strong>. Notes,
            checklists, maps, grids, and links cannot be added, and its
            achievements are excluded from Dashboard and Statistics counts.
            Existing content is preserved but read-only for new items.
          </span>
        </div>
      )}

      {/* One DndContext wraps ALL note-related interactive regions so
          note cards inside the SortableContext can also be dropped onto
          the organizer/subgroup chips (which are useDroppable targets).
          Otherwise the chips can't register drops from sortable items.
          - `pointerWithin` collision detection lets the small chip drop
            zones register hits even when a wide note-row overlaps many
            things at once; `closestCenter` was picking the sortable
            item's own center instead of the chip under the cursor.
          - `snapCenterToCursor` modifier keeps the drag preview under
            the pointer, fixing the "grab position vs hit position way
            off" complaint when grabbing a small left-edge grip on a
            wide row. */}
      <DndContext
        sensors={dndSensors}
        collisionDetection={noteAreaCollisionDetection}
        onDragEnd={handleCombinedDragEnd}
        modifiers={[snapCenterToCursor]}
      >

      {notesExpanded && noteOrganizers.length > 0 && (
        <div className="mb-2 flex items-center gap-1 flex-wrap" data-testid="note-organizer-filter">
          <span className="text-[10px] uppercase tracking-wider text-white/40 mr-1">Filter by subject:</span>
          {/* "All" chip is not a drop target — dropping there would have no meaningful assignment. */}
          <button
            onClick={() => { setNotesFilterId(null); setNotesFilterSubId(null); }}
            className={`px-2 py-0.5 rounded text-xs border transition-colors ${
              notesFilterId === null
                ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                : "bg-white/5 text-white/60 border-white/10 hover:text-white hover:bg-white/10"
            }`}
            data-testid="note-filter-all"
          >
            All
          </button>
          {/* Each organizer chip is BOTH a filter button and a drop target. */}
          {noteOrganizers.map(org => (
            <DroppableChip
              key={org.id}
              id={`note-organizer-drop:${org.id}`}
              data-testid={`note-filter-drop-${org.id}`}
              title={`Filter by, or drag a note here to file under "${org.name}"`}
            >
              <button
                onClick={() => { setNotesFilterId(org.id); setNotesFilterSubId(null); }}
                className={`px-2 py-0.5 rounded text-xs border transition-colors flex items-center gap-1 ${
                  notesFilterId === org.id
                    ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                    : "bg-white/5 text-white/60 border-white/10 hover:text-white hover:bg-white/10"
                }`}
                data-testid={`note-filter-${org.id}`}
              >
                <Folder className="w-3 h-3" />
                {org.name}
              </button>
            </DroppableChip>
          ))}
          <DroppableChip
            id="note-organizer-drop:__uncategorized__"
            data-testid="note-filter-drop-uncategorized"
            title="Filter by, or drag a note here to remove it from any organizer"
          >
            <button
              onClick={() => { setNotesFilterId("__uncategorized__"); setNotesFilterSubId(null); }}
              className={`px-2 py-0.5 rounded text-xs border transition-colors ${
                notesFilterId === "__uncategorized__"
                  ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                  : "bg-white/5 text-white/60 border-white/10 hover:text-white hover:bg-white/10"
              }`}
              data-testid="note-filter-uncategorized"
            >
              Uncategorized
            </button>
          </DroppableChip>
        </div>
      )}

      {/* Subgroup filter+drop chips — shown when the user has selected a
          specific organizer that has subgroups. Each chip is BOTH a
          filter (click to narrow to just this subgroup) AND a drop
          target (drag a note here to file it under that subgroup). */}
      {notesExpanded && notesFilterId && notesFilterId !== "__uncategorized__" && (() => {
        const activeOrg = noteOrganizers.find(o => o.id === notesFilterId);
        const subs = activeOrg?.subgroups || [];
        if (subs.length === 0) return null;
        return (
          <div className="mb-2 ml-2 flex items-center gap-1 flex-wrap" data-testid="note-subgroup-drops">
            <span className="text-[10px] uppercase tracking-wider text-white/40 mr-1">Subgroup:</span>
            <button
              onClick={() => setNotesFilterSubId(null)}
              className={`px-2 py-0.5 rounded text-xs border transition-colors ${
                notesFilterSubId === null
                  ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                  : "bg-white/5 text-white/50 border-white/10 hover:text-white hover:bg-white/10"
              }`}
              data-testid="note-subgroup-filter-all"
            >
              All in {activeOrg.name}
            </button>
            <DroppableChip
              id={`note-organizer-drop:${activeOrg.id}:__loose__`}
              data-testid={`note-subgroup-drop-loose`}
              title={`Click to filter, or drag a note here to remove it from any subgroup (still filed under "${activeOrg.name}")`}
            >
              <button
                onClick={() => setNotesFilterSubId("__loose__")}
                className={`px-2 py-0.5 rounded text-xs border transition-colors flex items-center gap-1 ${
                  notesFilterSubId === "__loose__"
                    ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                    : "bg-white/5 text-white/50 border-white/10 hover:text-white hover:bg-white/10"
                }`}
                data-testid="note-subgroup-filter-loose"
              >
                <Folder className="w-3 h-3 text-white/30" />
                Loose notes
              </button>
            </DroppableChip>
            {subs.map(sub => (
              <DroppableChip
                key={sub.id}
                id={`note-organizer-drop:${activeOrg.id}:${sub.id}`}
                data-testid={`note-subgroup-drop-${sub.id}`}
                title={`Click to filter, or drag a note here to file it under "${sub.name}"`}
              >
                <button
                  onClick={() => setNotesFilterSubId(sub.id)}
                  className={`px-2 py-0.5 rounded text-xs border transition-colors flex items-center gap-1 ${
                    notesFilterSubId === sub.id
                      ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                      : "bg-white/5 text-white/50 border-white/10 hover:text-white hover:bg-white/10"
                  }`}
                  data-testid={`note-subgroup-filter-${sub.id}`}
                >
                  <Folder className="w-3 h-3 text-amber-300/60" />
                  {sub.name}
                </button>
              </DroppableChip>
            ))}
          </div>
        );
      })()}

      {notesExpanded && (
        <div
          className={`grid gap-2 min-w-0 ${
            columns === 1
              ? "grid-cols-1"
              : columns === 2
              ? "grid-cols-1 md:grid-cols-2"
              : columns === 3
              ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
              : "grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
          }`}
          data-testid="content-list"
        >
          {showNewGridForm && (
            <Card className="bg-[#121212] border-emerald-500/20">
              <CardContent className="p-3 flex items-center gap-2">
                <Grid3X3 className="w-4 h-4 text-emerald-400 shrink-0" />
                <Input
                  value={newGridName}
                  onChange={e => setNewGridName(e.target.value)}
                  placeholder="Grid name..."
                  className="bg-black/50 border-white/10 text-white text-sm h-7 flex-1"
                  autoFocus
                  onKeyDown={e => e.key === "Enter" && createGrid()}
                  data-testid="new-grid-name-input"
                />
                <Button size="sm" onClick={createGrid} disabled={gridSaving} className="bg-emerald-600 hover:bg-emerald-500 h-7 text-xs" data-testid="create-grid-btn">
                  {gridSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Create"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setShowNewGridForm(false); setNewGridName(""); }} className="h-7 text-xs text-white/40">
                  <X className="w-3 h-3" />
                </Button>
              </CardContent>
            </Card>
          )}

          {notes.length === 0 && links.length === 0 && scorecards.length === 0 && !showNewGridForm && (
            game?.isAchievementSlop ? (
              <p className="text-amber-300/70 text-sm py-2" data-testid="slop-notes-disabled-msg">
                Notes, checklists, maps, and grids are disabled for games flagged as Achievement Slop.
              </p>
            ) : (
              <p className="text-white/25 text-sm py-2">No content yet. Use the Add button to create notes, links, or grids.</p>
            )
          )}

          {(notes.length > 0 || links.length > 0 || scorecards.length > 0) && filteredSorted.length === 0 && !showNewGridForm && (
            <p className="text-white/25 text-sm py-2" data-testid="notes-no-results">
              No notes match your search or filter.
            </p>
          )}

            <SortableContext
              items={pagedItems.map(it => it.__id)}
              strategy={verticalListSortingStrategy}
            >
              {pagedItems.map((item) => {
                if (item.__kind === "note") {
                  const note = item;
                  return (
                    <SortableItem key={`note-${note.id}`} id={note.id}>
                      {note.noteType === "character" ? (
                        <CharacterDisplay
                          note={note}
                          onEdit={(n) => onEditCharacter?.(n)}
                          onDelete={onDeleteNote}
                          onTogglePlanner={onToggleNotePlanner}
                          achievements={achievements}
                          availableNotes={notes.filter((n) => (n.noteType || "note") === "note")}
                          headerExtras={<OrganizerMoveMenu item={note} organizers={noteOrganizers} onMove={moveNoteToOrganizer} testIdPrefix="char-note-" />}
                        />
                      ) : note.noteType === "map" ? (
                        <MapDisplay
                          note={note}
                          onTogglePin={onToggleMapPin}
                          onEdit={(n) => { setEditingMap(n); setMapEditorOpen(true); }}
                          onDelete={onDeleteNote}
                          onSubmitToCommunity={onSubmitMapToCommunity}
                          onPinToPlanner={(n) => onToggleNotePlanner?.(n.id)}
                          headerExtras={<OrganizerMoveMenu item={note} organizers={noteOrganizers} onMove={moveNoteToOrganizer} testIdPrefix="map-note-" />}
                        />
                      ) : note.noteType === "checklist" ? (
                        <div className="relative">
                          {note.isPrivate && (
                            <span className="absolute top-2 right-2 z-10" title="Private - hidden when shared">
                              <Lock className="w-3 h-3 text-red-400/60" />
                            </span>
                          )}
                          <ChecklistDisplay
                            checklist={note}
                            onToggleItem={onToggleChecklistItem}
                            onShare={() => setShareChecklist(note)}
                            onEdit={() => setEditingChecklist(note)}
                            onDelete={onDeleteNote}
                            onSavePrefs={onSaveChecklistPrefs}
                            characters={(game?.notes || []).filter(n => n.noteType === "character")}
                            achievements={achievements}
                            headerExtras={<OrganizerMoveMenu item={note} organizers={noteOrganizers} onMove={moveNoteToOrganizer} testIdPrefix="checklist-note-" />}
                          />
                        </div>
                      ) : (
                        <Card className="bg-[#121212] border-white/5 hover:border-amber-500/30 transition-colors" data-testid={`note-card-${note.id}`}>
                          <CardContent className="p-3">
                            <div className="flex items-start gap-2">
                              <StickyNote className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  {note.title ? (
                                    <button
                                      onClick={() => {
                                        setExpandedNoteIds(prev => {
                                          const next = new Set(prev);
                                          next.has(note.id) ? next.delete(note.id) : next.add(note.id);
                                          return next;
                                        });
                                      }}
                                      className="flex items-center gap-1 text-white/90 font-medium text-sm hover:text-amber-300 transition-colors text-left"
                                      data-testid={`note-title-${note.id}`}
                                    >
                                      <ChevronRight className={`w-3.5 h-3.5 transition-transform shrink-0 ${expandedNoteIds.has(note.id) ? "rotate-90" : ""}`} />
                                      {note.title}
                                    </button>
                                  ) : null}
                                  {note.isPrivate && <Lock className="w-3 h-3 text-red-400/60 shrink-0" title="Private" />}
                                  <div className="ml-auto flex items-center gap-1 shrink-0">
                                  {onToggleNotePlanner && (
                                    <button
                                      onClick={() => onToggleNotePlanner(note.id)}
                                      className={`shrink-0 transition-colors p-0.5 ${note.pinnedToPlanner ? "text-cyan-400" : "text-white/50 hover:text-cyan-400"}`}
                                      title={note.pinnedToPlanner ? "Unpin from planner" : "Pin to planner"}
                                      data-testid={`pin-note-planner-${note.id}`}
                                    >
                                      <Pin className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => openEditNote(note)}
                                    className="text-white/50 hover:text-amber-400 transition-colors shrink-0 p-0.5"
                                    title="Edit note"
                                    data-testid={`edit-note-${note.id}`}
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </button>
                                  {noteOrganizers.length > 0 && (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <button
                                          className="text-white/50 hover:text-amber-300 transition-colors shrink-0 p-0.5"
                                          title={
                                            note.noteOrganizerId
                                              ? `Filed under: ${noteOrganizers.find(o => o.id === note.noteOrganizerId)?.name || "Organizer"}`
                                              : "File in an organizer"
                                          }
                                          data-testid={`move-note-${note.id}`}
                                        >
                                          {note.noteOrganizerId ? <FolderOpen className="w-3.5 h-3.5 text-amber-400/70" /> : <Folder className="w-3.5 h-3.5" />}
                                        </button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end" className="bg-[#0A0A0A] border-white/10 min-w-[180px]">
                                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-white/40">
                                          File in organizer
                                        </DropdownMenuLabel>
                                        <DropdownMenuItem
                                          onClick={() => moveNoteToOrganizer(note, "", "")}
                                          className={`text-xs ${!note.noteOrganizerId ? "bg-white/5 text-white" : "text-white/70"}`}
                                          data-testid={`move-note-${note.id}-uncategorized`}
                                        >
                                          Uncategorized
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator className="bg-white/5" />
                                        {noteOrganizers.map(org => (
                                          (org.subgroups && org.subgroups.length > 0) ? (
                                            <DropdownMenuSub key={org.id}>
                                              <DropdownMenuSubTrigger className="text-xs text-white/70 focus:text-white focus:bg-white/5">
                                                <Folder className="w-3 h-3 mr-1.5 text-amber-400/70" />
                                                {org.name}
                                              </DropdownMenuSubTrigger>
                                              <DropdownMenuSubContent className="bg-[#0A0A0A] border-white/10 min-w-[180px]">
                                                <DropdownMenuItem
                                                  onClick={() => moveNoteToOrganizer(note, org.id, "")}
                                                  className={`text-xs ${note.noteOrganizerId === org.id && !note.noteSubgroupId ? "bg-white/5 text-white" : "text-white/70"}`}
                                                >
                                                  <span className="italic text-white/50">(no subgroup)</span>
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator className="bg-white/5" />
                                                {org.subgroups.map(sub => (
                                                  <DropdownMenuItem
                                                    key={sub.id}
                                                    onClick={() => moveNoteToOrganizer(note, org.id, sub.id)}
                                                    className={`text-xs ${note.noteOrganizerId === org.id && note.noteSubgroupId === sub.id ? "bg-white/5 text-white" : "text-white/70"}`}
                                                  >
                                                    {sub.name}
                                                  </DropdownMenuItem>
                                                ))}
                                              </DropdownMenuSubContent>
                                            </DropdownMenuSub>
                                          ) : (
                                            <DropdownMenuItem
                                              key={org.id}
                                              onClick={() => moveNoteToOrganizer(note, org.id, "")}
                                              className={`text-xs ${note.noteOrganizerId === org.id ? "bg-white/5 text-white" : "text-white/70"}`}
                                              data-testid={`move-note-${note.id}-org-${org.id}`}
                                            >
                                              <Folder className="w-3 h-3 mr-1.5 text-amber-400/70" />
                                              {org.name}
                                            </DropdownMenuItem>
                                          )
                                        ))}
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  )}
                                  {onDeleteNote && (
                                    <button
                                      onClick={() => onDeleteNote(note.id)}
                                      className="text-white/50 hover:text-red-400 transition-colors shrink-0 p-0.5"
                                      title="Delete note"
                                      data-testid={`delete-note-${note.id}`}
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                  </div>
                                </div>
                                {(!note.title || expandedNoteIds.has(note.id)) && note.imageUrl && (
                                  <div className="mt-2">
                                    <img
                                      src={note.imageUrl}
                                      alt=""
                                      className="max-w-full max-h-64 w-auto h-auto rounded border border-white/10 object-contain"
                                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                                    />
                                  </div>
                                )}
                                {(!note.title || expandedNoteIds.has(note.id)) && (
                                  <div className="text-white/70 text-sm mt-1 whitespace-pre-wrap break-words">
                                    <RichText text={note.text} />
                                  </div>
                                )}
                                {(!note.title || expandedNoteIds.has(note.id)) && (
                                  <p className="text-white/30 text-xs mt-2">
                                    {note.createdAt ? new Date(note.createdAt).toLocaleDateString() : ""}
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </SortableItem>
                  );
                }

                if (item.__kind === "grid") {
                  const sc = item;
                  return (
                    <SortableItem key={`grid-${sc.id}`} id={sc.id}>
                      <Card className="bg-[#121212] border-white/5 hover:border-emerald-500/30 transition-colors group" data-testid={`grid-card-${sc.id}`}>
                        <CardContent className="p-0">
                          <div className="flex items-center gap-2 px-3 py-2.5">
                            <Grid3X3 className="w-4 h-4 text-emerald-400" />
                            <button onClick={() => setExpandedGrids(prev => ({ ...prev, [sc.id]: !prev[sc.id] }))} className="text-white/40 hover:text-white">
                              {expandedGrids[sc.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                            <span className="text-sm text-white font-medium flex-1 truncate">{sc.name}</span>
                            <span className="text-white/20 text-xs">{sc.rows?.length || 0}x{sc.cols?.length || 0}</span>
                            <OrganizerMoveMenu item={sc} organizers={noteOrganizers} onMove={moveGridToOrganizer} testIdPrefix="grid-" />
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-white/50 hover:text-red-400"
                              onClick={() => deleteGrid(sc.id, sc.name)} data-testid={`delete-grid-${sc.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                          {expandedGrids[sc.id] && (
                            <div className="px-3 py-3 border-t border-white/5">
                              <ScorecardEditor scorecard={sc} onSave={(data) => saveGrid(sc.id, data)} characters={(game?.notes || []).filter(n => n.noteType === "character")} achievements={achievements} notes={(game?.notes || []).filter(n => (n.noteType || "note") === "note")} masterGameId={game?.masterGameId} />
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </SortableItem>
                  );
                }

                if (item.__kind === "link") {
                  const link = item;
                  const linkExpanded = expandedLinks?.[link.id];
                  return (
                    <SortableItem key={`link-${link.id}`} id={link.id}>
                      <Card className="bg-[#121212] border-white/5 hover:border-blue-500/30 transition-colors group" data-testid={`link-card-${link.id}`}>
                        <CardContent className="p-0">
                          <div className="px-3 py-2.5 flex items-center gap-2">
                            <Link2 className="w-4 h-4 text-blue-400 shrink-0" />
                            <button
                              onClick={() => setExpandedLinks(prev => ({ ...prev, [link.id]: !prev?.[link.id] }))}
                              className="text-white/40 hover:text-white"
                              data-testid={`link-toggle-${link.id}`}
                            >
                              {linkExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                            <a
                              href={link.url} target="_blank" rel="noopener noreferrer"
                              className="flex-1 min-w-0 text-blue-400 hover:text-blue-300 text-sm truncate"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {link.title}
                            </a>
                            {link.isPrivate && <Lock className="w-3 h-3 text-red-400/60 shrink-0" title="Private" />}
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-white/50 hover:text-yellow-400"
                              onClick={() => openEditLink(link)} data-testid={`edit-link-${link.id}`}>
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                            <OrganizerMoveMenu item={link} organizers={noteOrganizers} onMove={moveLinkToOrganizer} testIdPrefix="link-" />
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-white/50 hover:text-red-400"
                              onClick={() => onDeleteLink?.(link.id)} data-testid={`delete-link-${link.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                          {linkExpanded && (
                            <div className="px-3 pb-2.5 pt-2 border-t border-white/5 space-y-1.5">
                              {link.description && (
                                <p className="text-[12px] text-white/70 whitespace-pre-wrap break-words">
                                  {link.description}
                                </p>
                              )}
                              <p className="text-[11px] text-white/40 break-all">{link.url}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </SortableItem>
                  );
                }
                return null;
              })}
            </SortableContext>
        </div>
      )}

      {notesExpanded && pageCount > 1 && (
        <div className="flex items-center justify-center gap-2 mt-3" data-testid="notes-pagination">
          <Button
            variant="ghost"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="h-7 px-2 text-xs text-white/60 hover:text-white hover:bg-white/5 disabled:opacity-30"
            data-testid="notes-page-prev"
          >
            <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Prev
          </Button>
          <span className="text-xs text-white/50" data-testid="notes-page-indicator">
            Page {page} of {pageCount}
            <span className="text-white/30"> · {filteredSorted.length} item{filteredSorted.length === 1 ? "" : "s"}</span>
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={page >= pageCount}
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            className="h-7 px-2 text-xs text-white/60 hover:text-white hover:bg-white/5 disabled:opacity-30"
            data-testid="notes-page-next"
          >
            Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      )}
      </DndContext>

      {/* Link Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={(open) => {
        setLinkDialogOpen(open);
        if (!open) { setEditingLink(null); setNewLinkTitle(""); setNewLinkUrl(""); setNewLinkDescription(""); setLinkIsPrivate(false); }
      }}>
        <DialogContent className="bg-[#0A0A0A] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">{editingLink ? "Edit Link" : "Add Link"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white/70 text-sm">Title</Label>
              <Input value={newLinkTitle} onChange={(e) => setNewLinkTitle(e.target.value)}
                placeholder="e.g., Achievement Guide, Walkthrough..."
                className="mt-1 bg-black/50 border-white/10 text-white" data-testid="link-title-input" />
            </div>
            <div>
              <Label className="text-white/70 text-sm">URL</Label>
              <Input value={newLinkUrl} onChange={(e) => setNewLinkUrl(e.target.value)}
                placeholder="https://..." className="mt-1 bg-black/50 border-white/10 text-white" data-testid="link-url-input" />
            </div>
            <div>
              <Label className="text-white/70 text-sm">Description <span className="text-white/30">(optional)</span></Label>
              <Textarea value={newLinkDescription} onChange={(e) => setNewLinkDescription(e.target.value)}
                placeholder="What is this link for? (Shown when the link card is expanded.)"
                className="mt-1 bg-black/50 border-white/10 text-white min-h-[60px]" data-testid="link-desc-input" />
            </div>
            <button type="button" onClick={() => setLinkIsPrivate(!linkIsPrivate)}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border transition-colors ${
                linkIsPrivate ? "bg-red-500/10 border-red-500/30 text-red-400" : "bg-white/5 border-white/10 text-white/40 hover:text-white/60"
              }`} data-testid="link-private-toggle">
              {linkIsPrivate ? <Lock className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {linkIsPrivate ? "Private" : "Visible when shared"}
            </button>
            <div className="flex justify-between">
              {editingLink && (
                <Button variant="destructive" size="sm" onClick={() => {
                  onDeleteLink(editingLink.id); setLinkDialogOpen(false); setEditingLink(null); setNewLinkTitle(""); setNewLinkUrl(""); setNewLinkDescription(""); setLinkIsPrivate(false);
                }} data-testid="link-delete-btn">Delete</Button>
              )}
              <Button onClick={handleSaveLink} disabled={!newLinkTitle.trim() || !newLinkUrl.trim()}
                className="ml-auto bg-blue-600 hover:bg-blue-700" data-testid="link-save-btn">
                {editingLink ? "Save" : "Add Link"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Note Dialogs */}
      <NoteOrganizersDialog
        open={organizersDialogOpen}
        onOpenChange={setOrganizersDialogOpen}
        gameId={game?.id}
        api={API}
        onChanged={fetchNoteOrganizers}
      />
      <AddNoteDialog
        open={noteDialogOpen}
        onOpenChange={(open) => {
          setNoteDialogOpen(open);
          if (!open) { setEditingNote(null); setNewNoteText(""); }
        }}
        noteText={newNoteText}
        setNoteText={setNewNoteText}
        onSave={handleSaveNote}
        checklistTemplates={checklistTemplates}
        onSaveChecklist={onAddChecklist}
        editingNote={editingNote}
        initialNoteType={initialNoteType}
        characters={(game?.notes || []).filter(n => n.noteType === "character")}
        achievements={achievements}
      />
      <EditChecklistDialog
        open={!!editingChecklist}
        onOpenChange={(open) => !open && setEditingChecklist(null)}
        checklist={editingChecklist}
        onSave={onEditChecklist}
        characters={(game?.notes || []).filter(n => n.noteType === "character")}
        achievements={achievements}
      />
      <ShareChecklistDialog
        open={!!shareChecklist}
        onOpenChange={(open) => !open && setShareChecklist(null)}
        checklist={shareChecklist}
        onShare={onShareChecklist}
      />

      {/* Map Editor Dialog */}
      {mapEditorOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e) => { if (e.target === e.currentTarget) { setMapEditorOpen(false); setEditingMap(null); } }}>
          <div className="bg-[#0A0A0F] border border-white/10 rounded-xl p-4 w-full max-w-2xl max-h-[85vh] overflow-y-auto mx-4" onMouseDown={(e) => e.stopPropagation()}>
            <h3 className="text-white font-medium text-sm mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-emerald-400" />
              {editingMap ? "Edit Map" : "Create Map"}
            </h3>
            <MapEditor
              initialData={editingMap}
              API={API}
              gameId={game?.id}
              onSave={(data) => {
                if (editingMap) {
                  onEditMap?.(editingMap.id, data);
                } else {
                  onAddMap?.(data);
                }
                setMapEditorOpen(false);
                setEditingMap(null);
              }}
              onCancel={() => { setMapEditorOpen(false); setEditingMap(null); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
